import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { ListFilter, Search } from "lucide-react";
import { Button } from "./Button";

// 表頭上的漏斗篩選(業主原話 2026-09-13:「應該是要直接在表頭 有一下漏斗
// (意思就是篩選的意思) 點要顯示的 這樣才對」)。
//
// 舊做法是把**所有選項攤成一排勾選框**放在表格外面。選項可以無限新增
// (品項庫的分類就是,設計規格 §5.2),一多那一排就把畫面推爆 ——
// 業主的原話是「如果有多分類 那邊會直接爆掉」。
//
// 這個元件之所以不會爆版,關鍵只有一條:
//
//   **浮層有固定的高度上限,而且自己捲動。**
//
// 四十個選項也只是在那 16rem 裡面捲,表格的版面一格都不會被推開。
// 高度上限與 overflow 寫成**行內樣式**而不是 class:它是這個元件的行為,
// 不是可以被外面一句 class 覆蓋掉的裝飾,而且測試量得到。
//
// 另外三件不能省的事:
//
// 1. **portal 到 body + position: fixed。** 表格外層是 overflow-x-auto,
//    CSS 規範說 overflow-x 非 visible 時 overflow-y 的 visible 會被算成 auto,
//    絕對定位的浮層會被那個容器裁掉 —— RowMenu 已經踩過一次這個坑。
// 2. **關閉後焦點回到漏斗。** 否則鍵盤使用者一收起浮層就被丟回頁首。
// 3. **勾了幾個要用數字說。** 不可以只靠漏斗圖示變色暗示「現在有在篩」,
//    色弱與單色列印都讀不出來。

/** 浮層寬度。選項多半是二到六個中文字,固定寬度比自適應穩。 */
const PANEL_WIDTH = 232;
/**
 * 選項區的高度上限。**這就是「選項再多也不爆版」的成因**,
 * 超過的部分在浮層裡面捲,不會把表格推開。
 */
const LIST_MAX_HEIGHT = "16rem";
/** 浮層連同搜尋框與底部按鈕的概估高度,用來判斷要往下開還是往上翻。 */
const PANEL_MAX_HEIGHT = 384;
/** 選項區換算成像素,用來判斷這個方向塞不塞得下完整的一份。 */
const LIST_MAX_HEIGHT_PX = 256;
/** 浮層扣掉選項區之後的那些固定高度:內距、搜尋列、兩道間距、底部按鈕列與邊框。 */
const PANEL_CHROME = 112;
/** 選項區再怎麼壓也要看得到兩三列,不然那一塊等於沒有。 */
const MIN_LIST_HEIGHT = 72;
/** 浮層再怎麼擠也不低於這個高度。比這更矮就沒有「篩選」可言了。 */
const MIN_PANEL_HEIGHT = 180;
/** 浮層與視窗邊緣之間留的空隙。 */
const VIEWPORT_GUTTER = 12;

/**
 * 一個選項:**送給後端的值**與**給人看的字**分開的那一種。
 *
 * 品項庫的分類兩者剛好相同(篩的就是分類名稱),報價單的客戶與同仁則不是 ——
 * 送出去的是 id、畫面上要顯示的是名字。直接拿名字當值會在同名的兩家公司上出錯,
 * 拿 id 當顯示則是給使用者看一串數字。
 */
export interface HeaderFilterOption {
  /** 勾選之後真正送出去的值,例如客戶 id。 */
  value: string;
  /** 浮層裡顯示的字,搜尋框也是比對這個。 */
  label: string;
}

/** 兩種寫法都收:純字串(值與顯示同一個字)與 `{value,label}`。 */
export type HeaderFilterOptions = ReadonlyArray<string | HeaderFilterOption>;

export interface HeaderFilterProps {
  /**
   * 這一欄的名稱,例如「分類」。
   * 無障礙名稱由它組出來:「篩選分類」「搜尋分類」「分類選項」。
   */
  label: string;
  /** 全部可選的項目,順序就是浮層裡列出來的順序。 */
  options: HeaderFilterOptions;
  /** 目前勾選的**值**(不是顯示的字)。 */
  selected: string[];
  /** 勾選變動時回呼。傳回的陣列一律照 `options` 的順序排,不是點擊的先後。 */
  onChange: (next: string[]) => void;
}

/** 把兩種寫法收斂成同一個形狀,底下就只要認得 `{value,label}` 一種。 */
function toOption(option: string | HeaderFilterOption): HeaderFilterOption {
  return typeof option === "string" ? { value: option, label: option } : option;
}

export function HeaderFilter({ label, options, selected, onChange }: HeaderFilterProps) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [style, setStyle] = useState<CSSProperties | null>(null);
  // 選項區真正能用的高度。視窗夠高時就是 16rem,矮的時候跟著縮 ——
  // 縮的是清單,搜尋列與底部的「全選 / 清除 / 完成」永遠留在畫面上。
  const [listHeight, setListHeight] = useState<string>(LIST_MAX_HEIGHT);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const panelId = `nq-header-filter-${useId()}`;

  const picked = useMemo(() => new Set(selected), [selected]);
  const count = selected.length;

  const items = useMemo(() => options.map(toOption), [options]);

  // 浮層裡的搜尋只過濾這份清單,不會送到後端 ——
  // 想找一個分類卻把整張表一起洗掉,那是兩件事混在一起。
  // 比對的是**顯示的字**:使用者看得到的是名字,不是 id。
  const shown = useMemo(() => {
    const needle = keyword.trim().toLowerCase();
    if (needle === "") return items;
    return items.filter((item) => item.label.toLowerCase().includes(needle));
  }, [items, keyword]);

  /**
   * 依漏斗的位置算出浮層的固定座標,下方空間不足時往上翻。
   * 這一步要在 setOpen 之前跑完,浮層才會在同一次 commit 裡帶著座標出現。
   */
  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor || typeof window === "undefined" || typeof anchor.getBoundingClientRect !== "function") {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
    // 靠左對齊漏斗,但不讓浮層掉出視窗外。
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8));
    // 這個方向真正剩多少高度。浮層是 fixed,落在視窗外的那一段捲不進視野 ——
    // 被切掉的搜尋框或「全選 / 清除 / 完成」就是完全碰不到,短視窗(375 斷點)
    // 一定會踩到。所以高度上限跟著可用空間算,不是固定那一個概估值。
    const available = (flipUp ? rect.top : window.innerHeight - rect.bottom) - VIEWPORT_GUTTER;
    const panelHeight = Math.max(MIN_PANEL_HEIGHT, available);
    const room = panelHeight - PANEL_CHROME;
    setListHeight(
      room >= LIST_MAX_HEIGHT_PX ? LIST_MAX_HEIGHT : `${Math.max(MIN_LIST_HEIGHT, room)}px`,
    );
    setStyle({
      position: "fixed",
      left,
      width: PANEL_WIDTH,
      // 視窗比浮層還窄時(手機橫放、分割視窗)寧可讓它縮,也不要橫向撐破畫面。
      maxWidth: "calc(100vw - 1rem)",
      maxHeight: panelHeight,
      // 上限之外還要捲得動,否則只是換一種方式把底部按鈕藏起來。
      overflowY: "auto",
      ...(flipUp ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) } : { top: rect.bottom + 4 }),
    });
  }, []);

  /** 關閉並把焦點還給漏斗 —— 鍵盤使用者不能被丟回頁首。 */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setKeyword("");
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const toggleOpen = () => {
    if (open) {
      close();
      return;
    }
    place();
    setKeyword("");
    setOpen(true);
  };

  // 開啟時焦點直接落在搜尋框:選項多的時候,用打字找比一路 Tab 下去快得多。
  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
  }, [open]);

  // 開著的時候跟著捲動與縮放走。用捕獲階段才收得到表格容器自己的捲動。
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // 點到浮層與漏斗以外的地方就收起來。這裡不還焦點:使用者是用滑鼠離開的。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(panelId)?.contains(target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, panelId, close]);

  /** 勾選與取消。順序跟著 options 走,不是使用者點擊的先後 —— 同一組勾選每次都
      組出同一個字串,查詢快取才不會因為順序不同而各存一份。 */
  const toggle = (value: string) => {
    const next = picked.has(value)
      ? items.filter((item) => item.value !== value && picked.has(item.value))
      : items.filter((item) => item.value === value || picked.has(item.value));
    onChange(next.map((item) => item.value));
  };

  /** 全選:把目前列出來的都勾起來。搜尋過就只勾看得到的那幾個,
      已經勾著的不會因為被搜尋條件濾掉而莫名其妙被取消。 */
  const selectAll = () => {
    const visible = new Set(shown.map((item) => item.value));
    onChange(
      items
        .filter((item) => visible.has(item.value) || picked.has(item.value))
        .map((item) => item.value),
    );
  };

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      // 浮層是浮在頁面上的,Tab 出去只會落在被它蓋住的表格上;而 Escape 綁在面板,
      // 焦點一離開面板就再也關不掉它。兩個鍵都直接收起來,焦點跟著還給漏斗。
      event.preventDefault();
      close();
    }
  };

  const panel =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={panelId}
            role="dialog"
            aria-label={`篩選${label}`}
            style={style}
            onKeyDown={onPanelKeyDown}
            className={
              "z-50 flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] " +
              "bg-[var(--surface)] p-2 shadow-card"
            }
          >
            <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] px-2">
              <Search aria-hidden="true" size={14} className="shrink-0 text-[var(--text-3)]" />
              <input
                ref={searchRef}
                type="search"
                value={keyword}
                aria-label={`搜尋${label}`}
                placeholder={`搜尋${label}…`}
                onChange={(event) => setKeyword(event.target.value)}
                className={
                  "h-8 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] " +
                  "placeholder:text-[var(--text-3)]"
                }
              />
            </div>

            {/* 選項區。**高度上限與自己捲動就寫在這裡** —— 選項再多也只在這一塊
                裡面捲,表格的版面一格都不會被推開。 */}
            <div
              role="group"
              aria-label={`${label}選項`}
              style={{ maxHeight: listHeight, overflowY: "auto" }}
              className="flex flex-col"
            >
              {shown.map((item) => (
                <label
                  key={item.value}
                  className={
                    "flex cursor-pointer items-center gap-2 rounded-[var(--radius)] px-1.5 py-1 " +
                    "text-sm text-[var(--text)] hover:bg-[var(--surface-2)]"
                  }
                >
                  <input
                    type="checkbox"
                    checked={picked.has(item.value)}
                    onChange={() => toggle(item.value)}
                    className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                  />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                </label>
              ))}
            </div>

            {/* 一個都沒對上時要說話。留一塊空白只會讓人以為畫面壞了。 */}
            {shown.length === 0 ? (
              <p className="m-0 px-1.5 py-1 text-sm text-[var(--text-3)]">沒有符合的{label}。</p>
            ) : null}

            <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] pt-2">
              <div className="flex items-center gap-1">
                <Button type="button" variant="ghost" size="sm" onClick={selectAll}>
                  全選
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
                  清除
                </Button>
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={() => close()}>
                完成
              </Button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // 勾了幾個要用**數字**說。只讓漏斗變色的話,色弱與單色列印都讀不出來 ——
        // 一個都沒勾時不顯示數字:那時候是「顯示全部」,擺一個 0 反而像是全被篩掉了。
        aria-label={count === 0 ? `篩選${label}` : `篩選${label},已勾選 ${count} 項`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggleOpen}
        className={
          "inline-flex items-center gap-1 rounded-[var(--radius)] px-1 py-0.5 align-middle " +
          (count === 0
            ? "text-[var(--text-3)] hover:bg-[var(--surface)] hover:text-[var(--text)]"
            : "text-[var(--text)] hover:bg-[var(--surface)]")
        }
      >
        <ListFilter aria-hidden="true" size={14} />
        {count > 0 ? (
          <span className="tabular text-[11px] font-semibold leading-none">{count}</span>
        ) : null}
      </button>
      {panel}
    </>
  );
}
